import React, { useEffect, useMemo, useState } from "react";
import { Building2, Check, Hash, KeyRound, Loader2, Search, Shield, Trash2, Users, X } from "lucide-react";
import { CommanServices } from "../services/commanServices";

const cleanList = (value) => (Array.isArray(value) ? value.map(String) : []);

const ChannelManagerModal = ({
  isOpen,
  isAdmin = false,
  onClose,
  incidentId,
  channel = null,
  initialUserIds = [],
  isScenario = false,
  onSaved,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [passkey, setPasskey] = useState("");
  const [clearPasskey, setClearPasskey] = useState(false);
  const [directory, setDirectory] = useState({ users: [], departments: [], teams: [], crews: [], roles: [] });
  const [selected, setSelected] = useState({ users: [], departments: [], teams: [], crews: [], roles: [] });
  const [tab, setTab] = useState("users");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isSystem = Boolean(channel?.system_key);
  const canProtect = channel?.system_key !== "incident";
  const hasPasskey = Boolean(channel?.requires_passkey);
  const initialUserIdsKey = useMemo(() => cleanList(initialUserIds).sort().join("|"), [initialUserIds]);

  useEffect(() => {
    if (!isOpen || !isAdmin) return;
    setName(channel?.name || "");
    setDescription(channel?.description || "");
    setPasskey("");
    setClearPasskey(false);
    setSelected({
      users: channel ? cleanList(channel?.user_ids) : cleanList(initialUserIds),
      departments: cleanList(channel?.department_ids),
      teams: cleanList(channel?.team_ids),
      crews: cleanList(channel?.crew_ids),
      roles: cleanList(channel?.role_names),
    });
    setTab("users");
    setSearch("");
    setError("");
    setLoading(true);
    Promise.all([
      CommanServices.getAppUsers().catch(() => ({ data: [] })),
      CommanServices.departments().catch(() => ({ data: [] })),
      CommanServices.getTeamsData().catch(() => ({ data: [] })),
    ]).then(([userRes, departmentRes, teamRes]) => {
      const users = Array.isArray(userRes?.data) ? userRes.data.filter((user) => user?.is_active !== false) : [];
      const departments = Array.isArray(departmentRes?.data) ? departmentRes.data : [];
      let teams = teamRes?.data || teamRes || [];
      if (!Array.isArray(teams)) teams = teams?.teams || [];
      const crews = teams.flatMap((team) => (team.crews || []).map((crew) => ({
        ...crew,
        team_name: team.team_name || team.name || "Team",
      })));
      const roles = [...new Set(users.flatMap((user) => {
        const value = user.role;
        return (Array.isArray(value) ? value : [value]).map((role) => String(role || "").trim()).filter(Boolean);
      }))].sort((a, b) => a.localeCompare(b));
      setDirectory({ users, departments, teams, crews, roles });
    }).finally(() => setLoading(false));
  }, [isOpen, isAdmin, channel?.id, initialUserIdsKey]);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = (value) => !query || String(value || "").toLowerCase().includes(query);
    if (tab === "users") {
      return directory.users
        .map((user) => ({ id: user.id, label: user.full_name || user.name || user.email, detail: user.email, icon: Users }))
        .filter((row) => matches(row.label) || matches(row.detail));
    }
    if (tab === "departments") {
      return directory.departments
        .map((department) => ({ id: department.id, label: department.department_name || department.name, detail: `${(department.members || []).length} members`, icon: Building2 }))
        .filter((row) => matches(row.label));
    }
    if (tab === "teams") {
      return directory.teams
        .map((team) => ({ id: team.id, label: team.team_name || team.name, detail: `${(team.crews || []).length} crews`, icon: Shield }))
        .filter((row) => matches(row.label));
    }
    if (tab === "crews") {
      return directory.crews
        .map((crew) => ({ id: crew.id, label: crew.crew_name || crew.name, detail: crew.team_name, icon: Shield }))
        .filter((row) => matches(row.label) || matches(row.detail));
    }
    return directory.roles
      .map((role) => ({ id: role, label: role, detail: "Dynamic role membership", icon: Hash }))
      .filter((row) => matches(row.label));
  }, [directory, tab, search]);

  const toggle = (id) => {
    setSelected((previous) => ({
      ...previous,
      [tab]: previous[tab].includes(String(id))
        ? previous[tab].filter((value) => value !== String(id))
        : [...previous[tab], String(id)],
    }));
  };

  const payload = () => {
    const data = {
      ...(!isSystem ? { name: name.trim() } : {}),
      description: description.trim(),
      user_ids: selected.users,
      department_ids: selected.departments,
      team_ids: selected.teams,
      crew_ids: selected.crews,
      role_names: selected.roles,
    };
    if (canProtect && passkey) data.passkey = passkey;
    if (canProtect && clearPasskey) data.clear_passkey = true;
    return data;
  };

  const save = async () => {
    if (!isAdmin) return;
    if (!isSystem && !name.trim()) {
      setError("Channel name is required.");
      return;
    }
    if (passkey && passkey.length < 6) {
      setError("Channel passkey must be at least 6 characters.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = channel
        ? await CommanServices.updateIncidentChannel(incidentId, channel.id, payload())
        : await CommanServices.createIncidentChannel(incidentId, payload());
      if (!result?.status) throw new Error(result?.message || "Could not save channel");
      onSaved?.(result.data);
      onClose();
    } catch (saveError) {
      setError(saveError?.message || "Could not save channel.");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!isAdmin || !channel || isSystem || saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await CommanServices.archiveIncidentChannel(incidentId, channel.id);
      if (!result?.status) throw new Error(result?.message || "Could not archive channel");
      onSaved?.(null, channel.id);
      onClose();
    } catch (archiveError) {
      setError(archiveError?.message || "Could not archive channel.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !isAdmin) return null;

  const selectionCount = Object.values(selected).reduce((total, values) => total + values.length, 0);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-700 bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-[#0a1a3f] px-6 py-5 text-white">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold"><Hash size={20} />{channel ? "Manage Channel" : "Create Channel"}</h2>
            <p className="mt-1 text-xs text-blue-100/70">
              Membership is controlled here on the web and never changes {isScenario ? 'scenario participation' : 'incident assignments'}.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-white/10"><X size={22} /></button>
        </div>

        <div className="overflow-y-auto p-6">
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500">Channel name</label>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isSystem}
            maxLength={80}
            placeholder="Example: Medical Operations"
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-indigo-500 disabled:bg-slate-100"
          />
          <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-slate-500">Purpose</label>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={240}
            rows={2}
            className="mt-1 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-indigo-500"
          />

          {canProtect ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-600">
                    <KeyRound size={15} className="text-indigo-600" />Optional channel passkey
                  </label>
                  <p className="mt-1 text-xs text-slate-500">
                    {hasPasskey
                      ? "Protected. Enter a new passkey to replace it, or leave this blank to keep it."
                      : "Assigned members enter it once, then the channel stays unlocked for their account."}
                  </p>
                </div>
                {hasPasskey ? (
                  <button
                    type="button"
                    onClick={() => { setClearPasskey((value) => !value); setPasskey(""); }}
                    className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold ${clearPasskey ? "bg-rose-600 text-white" : "bg-white text-rose-600 shadow-sm"}`}
                  >
                    {clearPasskey ? "Will remove" : "Remove passkey"}
                  </button>
                ) : null}
              </div>
              <input
                type="password"
                value={passkey}
                onChange={(event) => { setPasskey(event.target.value); setClearPasskey(false); }}
                disabled={clearPasskey}
                minLength={6}
                maxLength={128}
                autoComplete="new-password"
                placeholder={hasPasskey ? "New passkey (optional)" : "Passkey (optional)"}
                className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-indigo-500 disabled:bg-slate-100"
              />
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-medium text-emerald-800">
              <Shield size={16} />
              {isScenario ? 'Scenario Channel' : 'Incident Channel'} is always available to involved {isScenario ? 'participants' : 'members'} and cannot use a passkey.
            </div>
          )}

          <div className="mt-5 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Channel membership</h3>
              <p className="text-xs text-slate-500">You may include people and groups outside this {isScenario ? 'scenario' : 'incident'}.</p>
            </div>
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700">{selectionCount} selections</span>
          </div>

          <div className="mt-3 flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1">
            {["users", "departments", "teams", "crews", "roles"].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => { setTab(value); setSearch(""); }}
                className={`rounded-lg px-3 py-2 text-xs font-bold capitalize ${tab === value ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"}`}
              >
                {value} {selected[value].length ? `(${selected[value].length})` : ""}
              </button>
            ))}
          </div>

          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${tab}`} className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500" />
          </div>

          <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-slate-200">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500"><Loader2 className="animate-spin" size={18} />Loading directory…</div>
            ) : rows.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">No matches</p>
            ) : rows.map((row) => {
              const checked = selected[tab].includes(String(row.id));
              const Icon = row.icon;
              return (
                <button key={row.id} type="button" onClick={() => toggle(row.id)} className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50">
                  <span className={`flex h-5 w-5 items-center justify-center rounded border ${checked ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300"}`}>{checked ? <Check size={14} /> : null}</span>
                  <Icon size={17} className="text-indigo-500" />
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-slate-800">{row.label}</span>{row.detail ? <span className="block truncate text-xs text-slate-400">{row.detail}</span> : null}</span>
                </button>
              );
            })}
          </div>

          {channel?.members?.length ? (
            <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <summary className="cursor-pointer text-xs font-bold text-slate-600">Current resolved members ({channel.member_count})</summary>
              <div className="mt-2 flex flex-wrap gap-1.5">{channel.members.map((member) => <span key={member.id} className="rounded-full bg-white px-2.5 py-1 text-xs text-slate-600 shadow-sm">{member.name}</span>)}</div>
            </details>
          ) : null}

          {error ? <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">{error}</p> : null}
        </div>

        <div className="flex items-center gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          {channel && !isSystem ? <button type="button" onClick={archive} disabled={saving} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-rose-600 hover:bg-rose-50"><Trash2 size={16} />Archive</button> : <span />}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500">Cancel</button>
            <button type="button" onClick={save} disabled={saving || (!isSystem && !name.trim())} className="flex min-w-28 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelManagerModal;
